import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuList from '@mui/material/MenuList';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { SensorLatest } from '../../services/api';
import {
  getActiveMarker,
  getSelectedSensorIds,
  getSensorsById,
  getSortBy,
} from '../../state/selectors';
import { getBandColor, sortSensors, SensorFilteredStats, PlotType } from './helpers';
import useGlobalStyles from '../../styles';
import useStyles from '../../styles/calendarView';
import { setHighlightedItem, setSelectedSensors, setSortBy } from '../../state/actions';
import BaseArraySortMenu, { SortPropertyType } from '../HelperComponents/BaseArraySortMenu';
import { SortType } from '../../state/types';
import HistogramPlot from '../Plots/HistogramPlot';
import ReportsAddCircleIcon from '../../styles/icons/ReportsAddCircleIcon';
import BandValueCheckbox from '../HelperComponents/BandValueCheckbox';
import SensorIcon from '../../styles/icons/SensorIcon';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { themeProps } from '../../styles/theme';
import ValuePill from './ValuePill';
import InlineBarPlot from '../Plots/InlineBarPlot';

interface CalendarViewSensorArrayProps {
  sensorIds: string[];
  sensorFilteredStats: Map<string, SensorFilteredStats>;
  valuesRange: [number, number];
  summaryType: PlotType;
}

const SensorListItem = React.memo(
  ({
    sensor,
    sensorFilteredStats,
    valuesRange,
    showHistogram,
  }: {
    sensor: SensorLatest;
    sensorFilteredStats: Map<string, SensorFilteredStats>;
    valuesRange: [number, number];
    showHistogram: boolean;
  }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const classes = useStyles();
    const activeMarker = useAppSelector(getActiveMarker);
    const selectedSensorIds = useAppSelector(getSelectedSensorIds);
    const summaryValue = sensorFilteredStats.get(sensor.id)?.summaryValue;

    // storing hover state locally to avoid rendering all sensor
    const [isHovered, setIsHovered] = useState(false);
    const [menuAnchors, setMenuAnchors] = useState<Record<string, HTMLElement | null>>({});

    const color = getBandColor(summaryValue, activeMarker);
    const sensorStats = sensorFilteredStats.get(sensor.id)?.stats;
    let inlineValues;
    if (sensorStats?.totalAdded !== undefined) {
      inlineValues = sensorStats?.values?.totVals;
    } else if (sensorStats?.utl !== undefined) {
      inlineValues = sensorStats?.values?.utlVals.map((v) => v * 100);
    } else {
      inlineValues = sensorStats?.values?.avgVals;
    }

    const handleMouseEnter = () => {
      setIsHovered(true);
      dispatch(
        setHighlightedItem({ id: sensor.id, varName: activeMarker, source: 'dashboardPanel' })
      );
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      dispatch(setHighlightedItem({ id: '' }));
    };

    const handleMenuOpen = (sensorId: string, event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchors((prev) => ({
        ...prev,
        [sensorId]: event.currentTarget,
      }));
    };

    const handleMenuClose = (sensorId: string) => {
      setMenuAnchors((prev) => ({
        ...prev,
        [sensorId]: null,
      }));
    };

    const onMenuClick = (sensorId: string) => {
      handleMenuClose(sensorId);
      window.open(`/sensors/${sensorId}`, '_blank');
    };

    const selectSensorHandler = (id: string) => {
      const sensorsList = [...selectedSensorIds];
      const isSelectedSensor = sensorsList.some((sensorId) => sensorId === id);
      if (isSelectedSensor)
        dispatch(setSelectedSensors(sensorsList.filter((sensorId) => sensorId !== id)));
      else {
        sensorsList.push(id);
        dispatch(setSelectedSensors(sensorsList));
      }
    };

    return (
      <ListItem
        key={sensor.id}
        sx={{
          padding: '0',
          backgroundColor: isHovered ? `${color}66` : 'transparent',
          borderRadius: '8px',
          margin: '5px',
        }}
      >
        <ListItemButton
          onClick={() => selectSensorHandler(sensor.id)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleMouseEnter}
          onBlur={handleMouseLeave}
          sx={{ '&:hover': { background: 'none' } }}
        >
          <Grid container spacing={1}>
            <Grid item md={1} sm={1} xs={2} sx={{ textAlign: 'center' }}>
              <BandValueCheckbox sensorId={sensor.id} color={color} />
            </Grid>
            <Grid item md={2} sm={2} xs={5} sx={{ alignSelf: 'center' }}>
              <Typography variant="body1">{sensor.name}</Typography>
            </Grid>
            <Grid
              item
              md={1}
              sm={1}
              xs={5}
              sx={{ textAlign: { xs: 'end', sm: 'center' }, alignSelf: 'center' }}
            >
              <ValuePill value={summaryValue} />
            </Grid>
            <Grid item md={6} sm={6} xs={10} className={classes.centered}>
              {showHistogram ? (
                <HistogramPlot
                  values={inlineValues !== undefined ? inlineValues : []}
                  sensorName={sensor.name ?? 'UNKNOWN'}
                  varName={activeMarker}
                  xRange={valuesRange}
                />
              ) : (
                sensorStats &&
                !sensorStats.bandTimes?.has('Unknown') && (
                  <InlineBarPlot
                    data={sensorStats}
                    sensorName={sensor.name ?? 'UNKNOWN'}
                    varName={activeMarker}
                  />
                )
              )}
            </Grid>
          </Grid>
        </ListItemButton>
        <IconButton
          onClick={(event) => handleMenuOpen(sensor.id, event)}
          sx={{
            background: 'none',
            '&:hover': { background: theme.palette.primary.contrastText },
            marginRight: '10px',
          }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu
          id="item-menu"
          anchorEl={menuAnchors[sensor.id]}
          open={Boolean(menuAnchors[sensor.id])}
          onClose={() => handleMenuClose(sensor.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuList sx={{ padding: 0 }}>
            <MenuItem onClick={() => onMenuClick(sensor.id)}>
              <SensorIcon stroke={theme.palette.text.primary} />
              <span style={{ marginLeft: '5px' }}>Sensor Info</span>
            </MenuItem>
          </MenuList>
        </Menu>
      </ListItem>
    );
  }
);

function CalendarViewSensorArray({
  sensorIds,
  sensorFilteredStats,
  valuesRange,
  summaryType,
}: CalendarViewSensorArrayProps): JSX.Element {
  const globalClasses = useGlobalStyles();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const sortBy = useAppSelector(getSortBy);
  const sensorsById = useAppSelector(getSensorsById);
  const [showHistogram, setShowHistogram] = useState(true);

  const sensors = useMemo(
    () => sensorIds.map((id) => sensorsById.get(id) ?? ({ id } as SensorLatest)),
    [sensorIds, sensorsById]
  );

  useEffect(() => {
    const defaultSort: SortType = { property: 'name', ascending: true };
    if (sortBy !== defaultSort) dispatch(setSortBy(defaultSort));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedSensors = useMemo(
    () => sortSensors(sensors, sortBy, sensorFilteredStats),
    [sensors, sortBy, sensorFilteredStats]
  );

  const minMaxValue = useMemo(() => {
    let minValue: number | undefined;
    let maxValue: number | undefined;
    sortedSensors.forEach((sensor) => {
      const min = sensorFilteredStats.get(sensor.id)?.stats.maxMinAvg.min;
      const max = sensorFilteredStats.get(sensor.id)?.stats.maxMinAvg.max;
      if (min !== undefined && !Number.isNaN(min)) {
        if (minValue === undefined) minValue = min;
        else if (min < minValue) minValue = min;
      }
      if (max !== undefined && !Number.isNaN(max)) {
        if (maxValue === undefined) maxValue = max;
        else if (max > maxValue) maxValue = max;
      }
    });
    return { minValue, maxValue };
  }, [sensorFilteredStats, sortedSensors]);

  return (
    <div>
      <List>
        <ListItem sx={{ padding: '8px' }}>
          <Grid container spacing={1}>
            <Grid item md={1} sm={1} xs={2} sx={{ textAlign: 'center', alignSelf: 'center' }}>
              <Tooltip title="Add to compare" placement="top">
                <div>
                  <ReportsAddCircleIcon />
                </div>
              </Tooltip>
            </Grid>

            <Grid
              item
              md={2}
              sm={2}
              sx={{ alignSelf: 'center' }}
              className={globalClasses.hideInSmallScreen}
            >
              <Typography variant="h6">Name</Typography>
            </Grid>
            <Grid
              item
              md={2}
              sm={2}
              className={`${classes.centered} ${globalClasses.hideInSmallScreen}`}
            >
              <Typography variant="h6">{summaryType}</Typography>
            </Grid>
            <Grid
              item
              md={3}
              sm={3}
              xs={8}
              className={`${classes.centered} ${globalClasses.centeredFlex}`}
            >
              {minMaxValue.maxValue && <ValuePill value={minMaxValue.minValue} />}
              <Button
                onClick={() => setShowHistogram(!showHistogram)}
                variant={themeProps.btnVariant.default}
              >
                {showHistogram ? 'Histogram' : 'Bar'}
              </Button>
              {minMaxValue.maxValue && <ValuePill value={minMaxValue.maxValue} />}
            </Grid>
            <Grid item md={4} sm={4} xs={1} sx={{ textAlign: 'end' }}>
              <BaseArraySortMenu sortProperties={[SortPropertyType.name, SortPropertyType.value]} />
            </Grid>
          </Grid>
        </ListItem>
        {sortedSensors.length > 0 ? (
          sortedSensors?.map((sensor) => (
            <SensorListItem
              sensor={sensor}
              sensorFilteredStats={sensorFilteredStats}
              valuesRange={valuesRange}
              showHistogram={showHistogram}
            />
          ))
        ) : (
          <Typography variant="body1">No sensor data found</Typography>
        )}
      </List>
    </div>
  );
}

export default CalendarViewSensorArray;
